import React from "react";
import { Typography } from "@mui/material";
import PropTypes from "prop-types";

/**
 * CText - A wrapper for MUI Typography with additional functionality
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content of the text
 * @param {string} props.variant - Typography variant (h1, h2, h3, h4, h5, h6, subtitle1, subtitle2, body1, body2, etc.)
 * @param {string} props.align - Text alignment (left, right, center, justify)
 * @param {boolean} props.gutterBottom - If true, the text will have a bottom margin
 * @param {boolean} props.noWrap - If true, the text will not wrap, but truncate with an ellipsis
 * @param {boolean} props.paragraph - If true, the component will be a <p> element
 * @param {Object} props.sx - Additional styling for the text
 * @param {Object} props.theme - Theme object for styling
 * @param {boolean} props.strong - If true, the text will be bold (useful for emphasizing parts of text)
 * @param {React.ReactNode} props.icon - Icon to display before the text
 * @returns {JSX.Element}
 */
const CText = ({
  children,
  variant = "body1",
  align = "inherit",
  gutterBottom = false,
  noWrap = false,
  paragraph = false,
  sx = {},
  theme,
  strong = false,
  icon = null,
  ...rest
}) => {
  // Default styles based on theme (if provided)
  const defaultSx = theme
    ? {
        // Add any theme-specific default styling here
        fontFamily: theme.typography.fontFamily,
        ...(strong && { fontWeight: theme.typography.fontWeightBold || 700 }),
        display: icon ? 'flex' : 'block',
        alignItems: icon ? 'center' : 'inherit',
      }
    : {
        ...(strong && { fontWeight: 700 }),
        display: icon ? 'flex' : 'block',
        alignItems: icon ? 'center' : 'inherit',
      };

  // If the children are already wrapped in a <strong> tag and strong prop is true,
  // we don't need to add another level of strong styling
  const finalChildren = React.isValidElement(children) && children.type === 'strong' && strong
    ? children
    : strong && typeof children === 'string'
      ? <strong>{children}</strong>
      : children;

  // Render the icon with the text
  const content = icon ? (
    <>
      {React.cloneElement(icon, { 
        sx: { mr: 1, verticalAlign: 'text-bottom' },
        fontSize: variant === 'h5' || variant === 'h4' || variant === 'h3' ? 'large' : 'small'
      })}
      {finalChildren}
    </>
  ) : finalChildren;

  return (
    <Typography
      variant={variant}
      align={align}
      gutterBottom={gutterBottom}
      noWrap={noWrap}
      paragraph={paragraph}
      sx={{ ...defaultSx, ...sx }}
      {...rest}
    >
      {content}
    </Typography>
  );
};

CText.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
  gutterBottom: PropTypes.bool,
  noWrap: PropTypes.bool,
  paragraph: PropTypes.bool,
  sx: PropTypes.object,
  theme: PropTypes.object,
  strong: PropTypes.bool,
  icon: PropTypes.node,
};

export default CText;